import React, { useState, useEffect } from "react";
import { Button, Icon } from "abs-ui-components";
import { AbsDropDown, LoadingMessage } from "abs-ui-components";
import { emitToasterMsg } from "../../services/toasterUtility";
import { getDashboard } from "../../services/DashboardService";
import axiosHttp from "../../services/interceptor";
import "./ExportDcsDataPage.scss";

const ExportDcsDataPage = () => {
    const [wcnNumber, setWcnNumber] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [yearOptions, setYearOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingYears, setIsLoadingYears] = useState(true);

    useEffect(() => {
        fetchAvailableYears();
    }, []);

    const fetchAvailableYears = async () => {
        try {            
            setIsLoadingYears(true);
            const dashboardData = await getDashboard();
            
            if (dashboardData?.DashboardReportYears) {
                const years = dashboardData.DashboardReportYears
                    .map(x => ({ label: x.ReportYear, value: x.ReportYear }))
                    .sort((a, b) => b.value - a.value);
                
                setYearOptions(years);
                
                // Set default year to the most recent year with data
                if (years.length > 0) {
                    setSelectedYear(2024);
                }
            }
        } catch (error) {
            console.error("Error fetching years:", error);
            emitToasterMsg("error", "Error", "Failed to fetch available years");
            
            // Fallback to generate years list
            const currentYear = new Date().getFullYear();
            const startYear = 2018;
            const fallbackYears = [];
            
            for (let year = currentYear + 1; year >= startYear; year--) {
                fallbackYears.push({ label: year, value: year });
            }
            
            setYearOptions(fallbackYears);
        } finally {
            setIsLoadingYears(false);
        }
    };

    const handleExport = async () => {
        if (!wcnNumber.trim()) {
            emitToasterMsg("error", "Validation Error", "Please enter a WCN number");
            return;
        }

        if (!selectedYear) {
            emitToasterMsg("error", "Validation Error", "Please select a year");
            return;
        }

        try {
            setIsLoading(true);
            
            const response = await axiosHttp({
                url: `api/Report/ExportDcsDataByWcn?wcnNumber=${wcnNumber.trim()}&reportYear=${selectedYear}`,
                method: "GET",
                responseType: "blob",
            });

            // Create a blob from the response data
            const blob = new Blob([response.data], {
                type: response.headers["content-type"] || "application/zip"
            });

            // Extract filename from content-disposition header or use default
            const contentDisposition = response.headers["content-disposition"];
            let filename = `DCS_Reports_WCN_${wcnNumber}_${selectedYear}.zip`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

            emitToasterMsg("success", "Success", "DCS reports exported successfully as individual Excel files in a ZIP archive");
        } catch (error) {
            console.error("Export error:", error);
            
            if (error.response?.status === 404) {
                emitToasterMsg("error", "Error", "No DCS reports found for the specified WCN and year");
            } else if (error.response?.data) {
                // Try to read error message from blob
                try {
                    const errorText = await error.response.data.text();
                    const errorData = JSON.parse(errorText);
                    emitToasterMsg("error", "Error", errorData.errorMessage || "Failed to export data");
                } catch {
                    emitToasterMsg("error", "Error", "Failed to export data");
                }
            } else {
                emitToasterMsg("error", "Error", error.message || "Failed to export data");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleWcnChange = (e) => {
        setWcnNumber(e.target.value);
    };

    const handleYearChange = (selected) => {
        setSelectedYear(selected.value);
    };

    return (
        <div className="export-data-page">
            <div className="page-header">
                <h1 className="page-title">Export DCS Data</h1>
                <p className="page-subtitle">
                    Export DCS (Data Collection System) data for all vessels associated with a specific WCN (generates separate Excel files for each report, zipped together)
                </p>
            </div>

            <div className="export-form-container">
                <div className="form-card">
                    <div className="form-content">
                        <div className="form-group">
                            <label className="form-label">WCN Number</label>
                            <input
                                type="text"
                                placeholder="Enter WCN number"
                                value={wcnNumber}
                                onChange={handleWcnChange}
                                disabled={isLoading}
                                className="form-control wcn-input"
                            />
                            <span className="help-text">
                                Enter the 6-digit WCN number for which you want to export data
                            </span>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Reporting Year</label>
                            {isLoadingYears ? (
                                <div className="year-loading">
                                    <LoadingMessage message="Loading years..." showDots={true} />
                                </div>
                            ) : (
                                <>
                                    <AbsDropDown
                                        name="reportYear"
                                        handleChange={handleYearChange}
                                        selected={selectedYear}
                                        options={yearOptions}
                                        disabled={isLoading}
                                        className="year-dropdown"
                                    />
                                    <span className="help-text">
                                        Select the reporting year for the DCS data
                                    </span>
                                </>
                            )}
                        </div>

                        <div className="form-actions">
                            <Button
                                variant="secondary"
                                onClick={handleExport}
                                disabled={isLoading || isLoadingYears || !wcnNumber.trim()}
                                startIcon={<Icon icon="ios_share" />}
                            >
                                {isLoading ? "Please wait..." : "Export Data"}
                            </Button>
                        </div>

                        {isLoading && (
                            <div className="loading-overlay">
                                <LoadingMessage 
                                    message="Generating individual DCS Excel files and creating ZIP archive. This may take a few moments..." 
                                    showDots={true} 
                                />
                            </div>
                        )}
                    </div>

                    <div className="info-panel">
                        <h3 className="info-title">Export Information</h3>
                        <div className="info-content">
                            <p>This export will include the following worksheets for all vessels under the specified WCN:</p>
                            <ul className="info-list">
                                <li><strong>Vessel Detail:</strong> Complete vessel information</li>
                                <li><strong>BDN Summary – BDN (1):</strong> Bunker Delivery Note summary</li>
                                <li><strong>BDN Summary – Correction (2):</strong> Tank oil remaining corrections</li>
                                <li><strong>BDN Summary – Other (3):</strong> Other corrections</li>
                                <li><strong>BDN Summary – Consumption (1-2-3):</strong> Annual fuel consumption</li>
                                <li><strong>IMO DCS Data:</strong> Voyage-level DCS data with dynamic fuel columns</li>
                                <li><strong>ReportSummary-Vessel:</strong> Vessel details including engine data</li>
                                <li><strong>ReportSummary-CII:</strong> Carbon Intensity Indicator summary (reported values only)</li>
                                <li><strong>ReportSummary-Consumptions:</strong> Fuel consumption summary</li>
                                <li><strong>ReportSummary-Consump.WithoutUnderway:</strong> Consumption without hours underway</li>
                                <li><strong>ReportSummary-CII Corrections:</strong> CII corrections data</li>
                                <li><strong>ReportSummary-Exclusions:</strong> Exclusions data</li>
                            </ul>
                            <p className="info-note">
                                <Icon icon="info" className="info-icon" />
                                Each Excel file will contain comprehensive DCS data for one vessel report, 
                                with the Report ID in the format: 
                                DCS - [IMO Number] - [Year] - [ID]
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportDcsDataPage; 